import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/lib/ws";
import { useRideChat } from "@/hooks/useRideChat";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import MapView, { driverIcon, pickupIcon, destIcon } from "@/components/MapView";
import { RideChat } from "@/components/RideChat";
import { IncomingRequestModal } from "@/components/IncomingRequestModal";
import { DriverProfileModal, DriverHistorySheet } from "@/components/driver/DriverSheets";
import { MyDataCard, VehicleReadOnly, AdditionalDriversCard } from "@/components/driver/AccountPanel";
import { SubscriptionPanel } from "@/components/driver/SubscriptionPanel";
import { BottomNav } from "@/components/BottomNav";
import { Marker, Polyline } from "react-leaflet";
import { Taxi, SignOut, ClockCounterClockwise, Power, X, CheckCircle, Play, Clock, WarningOctagon, ChatCircleDots, Money, CreditCard, Crown, MapTrifold, Car } from "@phosphor-icons/react";

const DEFAULT_CENTER = [40.4168, -3.7038];
const NO_MARKERS = [];
const ROUTE_STYLE = { color: "#F5B700", weight: 4, opacity: 0.7 };
const REQUEST_TIMEOUT = 30;
const ACTIVE_PILL = {
  accepted: { className: "pill-info", text: "Yendo al pasajero" },
  in_progress: { className: "pill-online", text: "En viaje" },
  completed: { className: "pill-offline", text: "Completado" },
};

export default function DriverDashboard() {
  const { user, driverProfile, logout, refresh } = useAuth();
  const [center, setCenter] = useState(DEFAULT_CENTER);
  const [online, setOnline] = useState(false);
  const [incoming, setIncoming] = useState(null); // single targeted request
  const [reqLeft, setReqLeft] = useState(REQUEST_TIMEOUT);
  const [activeRide, setActiveRide] = useState(null);
  const [section, setSection] = useState("home"); // home | history | subscription | profile
  const [history, setHistory] = useState([]);
  const [panic, setPanic] = useState(null);
  const [subInfo, setSubInfo] = useState(null);
  const watchIdRef = useRef(null);
  const reqTimerRef = useRef(null);
  const driverAccountId = user?.parent_driver_id || user?.id;
  const { messages, showChat, setShowChat, unread, openChat, handleChatMessage, resetChat } =
    useRideChat({ activeRide, userId: driverAccountId });

  const refreshSub = useCallback(async () => {
    try {
      const { data } = await api.get("/subscription/me");
      setSubInfo(data);
    } catch (err) { logger.warn("[driver] subscription fetch failed", err); }
  }, []);
  useEffect(() => { refreshSub(); }, [refreshSub]);

  // Handle return from Stripe checkout: poll payment status, then refresh subscription.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/subscription/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshSub();
          setSection("subscription");
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
        if (data.status === "expired" || tries >= 6) {
          window.history.replaceState({}, "", window.location.pathname);
          return;
        }
      } catch (err) { logger.warn("[driver] checkout poll failed", err); }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refreshSub]);

  const refreshActive = useCallback(async () => {
    const { data } = await api.get("/rides/active");
    setActiveRide(data || null);
  }, []);

  const refreshIncoming = useCallback(async () => {
    if (!online) { setIncoming(null); return; }
    try {
      const { data } = await api.get("/rides/incoming");
      setIncoming((data && data.length) ? data[0] : null);
    } catch (err) { logger.warn("[driver] incoming fetch failed", err); }
  }, [online]);

  useEffect(() => { refreshActive(); }, [refreshActive]);
  useEffect(() => { refreshIncoming(); }, [refreshIncoming]);

  // Countdown for the incoming request. Depends only on the request id (a primitive)
  // so the timer restarts when a new request arrives, not on unrelated re-renders.
  const incomingId = incoming?.id;
  useEffect(() => {
    if (!incomingId) {
      clearInterval(reqTimerRef.current);
      return undefined;
    }
    setReqLeft(REQUEST_TIMEOUT);
    clearInterval(reqTimerRef.current);
    reqTimerRef.current = setInterval(() => {
      setReqLeft((s) => {
        if (s <= 1) { clearInterval(reqTimerRef.current); setIncoming(null); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(reqTimerRef.current);
  }, [incomingId]);

  const handleWs = useCallback((msg) => {
    if (msg.type === "ride_request_to_you") {
      setIncoming(msg.ride);
    } else if (msg.type === "ride_request_expired") {
      setIncoming((cur) => (cur?.id === msg.ride_id ? null : cur));
    } else if (msg.type === "ride_cancelled") {
      setIncoming((cur) => (cur?.id === msg.ride_id ? null : cur));
      if (activeRide?.id === msg.ride_id) { setActiveRide(null); resetChat(); }
    } else if (msg.type === "panic_alert") {
      setPanic(msg.alert);
    } else if (msg.type === "chat") {
      handleChatMessage(msg);
    }
  }, [activeRide, handleChatMessage, resetChat]);

  const { connected, send } = useWebSocket(handleWs, !!user);

  const sendChat = (text) => send({ type: "chat", ride_id: activeRide.id, text });

  // GPS tracking when online
  useEffect(() => {
    if (online && navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        (p) => {
          const lat = p.coords.latitude, lng = p.coords.longitude;
          setCenter([lat, lng]);
          send({ type: "location", lat, lng, heading: p.coords.heading });
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000 }
      );
      return () => navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, [online, send]);

  const toggleOnline = async () => {
    const newVal = !online;
    try {
      if (newVal && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (p) => {
          await api.post("/driver/location", { lat: p.coords.latitude, lng: p.coords.longitude });
          await api.post(`/driver/online?value=true`);
          setOnline(true);
        }, async () => {
          await api.post("/driver/location", { lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
          await api.post(`/driver/online?value=true`);
          setOnline(true);
        });
      } else {
        await api.post(`/driver/online?value=${newVal}`);
        setOnline(newVal);
        if (!newVal) setIncoming(null);
      }
    } catch (err) { logger.warn("[driver] online toggle failed", err); }
  };

  const acceptRide = async () => {
    if (!incoming) return;
    try {
      const { data } = await api.post(`/rides/${incoming.id}/accept`);
      setActiveRide(data);
      setIncoming(null);
    } catch (err) {
      setIncoming(null);
      logger.warn("[driver] accept ride failed", err);
    }
  };
  const rejectRide = async () => {
    if (!incoming) return;
    const id = incoming.id;
    setIncoming(null);
    try { await api.post(`/rides/${id}/reject`); } catch (err) { logger.warn("[driver] reject failed", err); }
  };
  const startRide = async () => {
    const { data } = await api.post(`/rides/${activeRide.id}/start`);
    setActiveRide(data);
  };
  const completeRide = async () => {
    const { data } = await api.post(`/rides/${activeRide.id}/complete`);
    setActiveRide(data);
    setShowChat(false);
    setTimeout(() => { setActiveRide(null); resetChat(); }, 3000);
  };

  const loadHistory = async () => {
    const { data } = await api.get("/rides/history");
    setHistory(data || []);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    await api.put("/driver/profile", {
      vehicle_model: fd.get("vehicle_model"),
      vehicle_plate: fd.get("vehicle_plate"),
      vehicle_color: fd.get("vehicle_color"),
      license_number: fd.get("license_number"),
      base_fee_day: parseFloat(fd.get("base_fee_day")),
      base_fee_night: parseFloat(fd.get("base_fee_night")),
      rate_per_km_day: parseFloat(fd.get("rate_per_km_day")),
      rate_per_km_night: parseFloat(fd.get("rate_per_km_night")),
    });
    await refresh();
  };

  // Load history when entering the Viajes tab
  useEffect(() => {
    if (section === "history") loadHistory();
  }, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const driverTabs = [
    { id: "home", label: "Inicio", icon: MapTrifold, testId: "driver-nav-home" },
    { id: "history", label: "Viajes", icon: ClockCounterClockwise, testId: "driver-nav-history" },
    { id: "subscription", label: "Suscripción", icon: Crown, testId: "driver-nav-subscription", badge: !!(subInfo && !subInfo.can_operate) },
    { id: "profile", label: "Vehículo", icon: Car, testId: "driver-nav-profile" },
  ];

  return (
    <div className="app-dark app-shell relative h-screen overflow-hidden" data-testid="driver-app">
      <div className="absolute inset-0">
        <MapView center={center} zoom={14} markers={NO_MARKERS} recenter>
          <Marker position={center} icon={driverIcon} />
          {activeRide && (
            <>
              <Marker position={[activeRide.pickup_lat, activeRide.pickup_lng]} icon={pickupIcon} />
              <Marker position={[activeRide.dest_lat, activeRide.dest_lng]} icon={destIcon} />
              <Polyline positions={activeRide.route_geometry?.length ? activeRide.route_geometry : [[activeRide.pickup_lat, activeRide.pickup_lng], [activeRide.dest_lat, activeRide.dest_lng]]} pathOptions={ROUTE_STYLE} />
            </>
          )}
        </MapView>
      </div>

      {/* Panic alert banner (passenger triggered) — contextual, any tab */}
      {panic && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[1200] bg-red-600 text-white rounded-2xl px-5 py-3 shadow-2xl flex items-center gap-3 panic-pulse max-w-[92vw]" data-testid="driver-panic-banner">
          <WarningOctagon size={26} weight="fill" />
          <div className="text-sm">
            <div className="font-bold">¡Emergencia del pasajero!</div>
            <div className="opacity-90">{panic.passenger_name} activó el botón de pánico.</div>
          </div>
          <button onClick={() => setPanic(null)} className="ml-2 bg-white/20 rounded-full p-1" data-testid="dismiss-panic-btn"><X size={16} /></button>
        </div>
      )}

      {/* Incoming targeted request popup — contextual, any tab */}
      {incoming && !activeRide && (
        <IncomingRequestModal incoming={incoming} reqLeft={reqLeft} onAccept={acceptRide} onReject={rejectRide} />
      )}

      {section === "home" && (
      <>
      {/* Top bar */}
      <div className="absolute top-4 left-4 right-4 z-50 flex items-center justify-between" data-testid="driver-topbar">
        <div className="glass rounded-full px-4 py-2 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Taxi size={18} color="#000" weight="duotone" />
          </div>
          <div>
            <div className="text-xs text-slate-500">Conductor</div>
            <div className="font-bold text-sm">{user?.name}</div>
          </div>
          <span className={`pill ${connected && online ? "pill-online" : "pill-offline"}`}>
            {connected && online && <span className="dot-pulse" />} {online ? "En línea" : "Desconectado"}
          </span>
        </div>
      </div>

      {/* Online toggle */}
      <div className="absolute top-24 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2">
        <button
          onClick={toggleOnline}
          className="glass rounded-full px-6 py-3 flex items-center gap-3 font-bold shadow-lg"
          style={{ background: online ? "rgba(16,185,129,0.95)" : "rgba(24,24,27,0.95)", color: "white" }}
          data-testid="driver-online-toggle"
        >
          <Power size={20} weight="bold" />
          {online ? "EN LÍNEA" : "DESCONECTADO"}
          {connected && online && <span className="dot-pulse" style={{ background: "white" }} />}
        </button>
        {subInfo && !subInfo.can_operate && (
          <button onClick={() => setSection("subscription")} className="bg-red-600 text-white rounded-full px-4 py-2 text-xs font-bold shadow-lg flex items-center gap-1.5 max-w-[90vw]" data-testid="subscription-blocked-banner">
            <WarningOctagon size={15} weight="fill" /> Sin suscripción activa: no recibirás viajes. Activar plan
          </button>
        )}
      </div>

      {/* Bottom sheet: idle / waiting */}
      {!activeRide && (
        <div className="bottom-sheet slide-up" data-testid="driver-bottom-sheet">
          <div className="handle" />
          <h2 className="text-2xl font-display font-bold mb-3">
            {online ? "Esperando solicitudes" : "Conéctate para recibir viajes"}
          </h2>
          <p className="text-slate-500 text-sm">
            {online
              ? "Cuando un pasajero te elija recibirás una solicitud directa con 30 segundos para aceptar."
              : 'Activa "EN LÍNEA" para que los pasajeros cercanos puedan elegirte.'}
          </p>
        </div>
      )}

      {/* Active ride */}
      {activeRide && (
        <div className="bottom-sheet slide-up" data-testid="driver-active-ride">
          <div className="handle" />
          <div className="flex justify-between items-center mb-3">
            {(() => {
              const s = ACTIVE_PILL[activeRide.status] || ACTIVE_PILL.completed;
              return <span className={`pill ${s.className}`}>{s.text}</span>;
            })()}
            <span className="font-display font-bold text-xl" style={{ color: "var(--primary)" }}>€{activeRide.estimated_price?.toFixed(2)}</span>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 mb-3">
            <div className="text-xs text-slate-500">Pasajero</div>
            <div className="font-bold">{activeRide.passenger_name}</div>
          </div>
          {activeRide.payment_method && (
            <div className="rounded-xl px-3 py-2.5 mb-3 flex items-center gap-2 font-bold" style={{ background: "var(--primary-soft)" }} data-testid="driver-payment-method">
              {activeRide.payment_method === "card" ? <CreditCard size={20} weight="duotone" /> : <Money size={20} weight="duotone" />}
              <span>Cobrar en {activeRide.payment_method === "card" ? "TARJETA" : "EFECTIVO"}</span>
            </div>
          )}
          {(activeRide.status === "accepted" || activeRide.status === "in_progress") && (
            <button onClick={openChat} className="btn-ghost w-full mb-3 flex items-center justify-center gap-2" data-testid="open-chat-btn">
              <ChatCircleDots size={18} weight="duotone" style={{ color: "var(--primary)" }} /> Mensaje al pasajero
              {unread > 0 && <span className="bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="chat-unread-badge">{unread}</span>}
            </button>
          )}
          <div className="space-y-2 mb-4 text-sm">
            <div className="flex gap-2 items-start"><div className="w-3 h-3 mt-1 rounded-full bg-emerald-500" /> <span>{activeRide.pickup_address}</span></div>
            <div className="flex gap-2 items-start"><div className="w-3 h-3 mt-1 rounded-full bg-red-500" /> <span>{activeRide.dest_address}</span></div>
          </div>
          {activeRide.status === "accepted" && (
            <button onClick={startRide} className="btn-primary w-full" data-testid="start-ride-btn">
              <Play size={16} className="inline mr-1" weight="fill" /> Iniciar viaje
            </button>
          )}
          {activeRide.status === "in_progress" && (
            <button onClick={completeRide} className="btn-primary w-full" data-testid="complete-ride-btn">
              <CheckCircle size={16} className="inline mr-1" weight="fill" /> Finalizar viaje
            </button>
          )}
        </div>
      )}

      {/* Profile modal */}
      </>
      )}

      {/* Tab sections */}
      {section === "history" && (
        <DriverHistorySheet embedded history={history} />
      )}
      {section === "subscription" && (
        <SubscriptionPanel embedded onChange={setSubInfo} />
      )}
      {section === "profile" && (
        <div className="app-section" data-testid="driver-profile-section">
          <h2 className="section-title font-display">Mi cuenta</h2>
          <MyDataCard user={user} onSaved={refresh} />
          <h2 className="section-title font-display mt-6">Mi vehículo</h2>
          {user?.is_additional ? (
            <VehicleReadOnly driverProfile={driverProfile} />
          ) : (
            <DriverProfileModal embedded driverProfile={driverProfile} onSubmit={saveProfile} />
          )}
          {!user?.is_additional && <AdditionalDriversCard />}
          <button onClick={logout} className="btn-ghost w-full text-red-500 mt-4" data-testid="driver-logout-btn">
            <SignOut size={18} className="inline mr-2" weight="duotone" /> Cerrar sesión
          </button>
        </div>
      )}

      {/* Ride chat (contextual) */}
      {showChat && activeRide && (
        <RideChat
          messages={messages}
          myId={driverAccountId}
          otherName={activeRide.passenger_name}
          onSend={sendChat}
          onClose={() => setShowChat(false)}
        />
      )}

      <BottomNav tabs={driverTabs} active={section} onChange={setSection} />
    </div>
  );
}
